import React, { useMemo } from 'react';
import { array, func, object, string } from 'prop-types';
import moment from 'moment';

import MenuExhibition from '../MenuExhibition/MenuExhibition';
import MenuEvent from '../MenuEvent/MenuEvent';
import { JOIN_MAILING_LIST_FORM_URL, SECTIONS } from '../../constants';

import 'react-toastify/dist/ReactToastify.css';
import './Menu.scss';

const Menu = ({
  currentExhibitions,
  upcomingExhibitions,
  archiveExhibitions,
  upcomingEvents,
  setIsSidebarOpen,
  setActiveSection,
  educationItems,
  podcastItems,
  publicationItems,
  staticText,
  language,
}) => {
  const upcomingEventsAndExhibitions = useMemo(
    () =>
      [
        ...upcomingExhibitions.map((item) => ({
          isExhibition: true,
          ...item,
        })),
        ...upcomingEvents.map((item) => ({ isEvent: true, ...item })),
      ].sort((a, b) => {
        if (
          moment(
            (a.isExhibition && a.dateFrom) || (a.isEvent && a.date),
            'YYYY-MM-DD',
          ).isAfter(
            moment(
              (b.isExhibition && b.dateFrom) || (b.isEvent && b.date),
              'YYYY-MM-DD',
            ),
          )
        ) {
          return 1;
        }
        if (
          moment(
            (b.isExhibition && b.dateFrom) || (b.isEvent && b.date),
            'YYYY-MM-DD',
          ).isAfter(
            moment(
              (a.isExhibition && a.dateFrom) || (a.isEvent && a.date),
              'YYYY-MM-DD',
            ),
          )
        ) {
          return -1;
        }
        return 0;
      }),
    [upcomingEvents, upcomingExhibitions],
  );

  const sectionLinks = [
    {
      section: SECTIONS.ARCHIVE,
      title: staticText.sidebarArchive,
      isVisible: !!archiveExhibitions.length,
    },
    {
      section: SECTIONS.EVENTS,
      title: staticText.sidebarEvents,
      isVisible: true,
    },
    {
      section: SECTIONS.PODCASTS,
      title: staticText.podcastSectionTitle,
      isVisible: !!podcastItems.length,
    },
    {
      section: SECTIONS.PUBLICATIONS,
      title: staticText.publicationSectionTitle,
      isVisible: !!publicationItems.length,
    },
    {
      section: SECTIONS.EDUCATION,
      title: staticText.educationSectionTitle,
      isVisible: !!educationItems.length,
    },
    {
      section: SECTIONS.ABOUT,
      title: staticText.sidebarAbout,
      isVisible: true,
    },
  ];

  const onClickSection = (section) => {
    setIsSidebarOpen(false);
    setActiveSection({ section });
  };

  const onSelectExhibition = (id) =>
    setActiveSection({ section: SECTIONS.EXHIBITION, id });

  const onSelectEvent = (id) =>
    setActiveSection({ section: SECTIONS.EVENT, id });

  const onClickJoinMailingList = () => {
    window.open(JOIN_MAILING_LIST_FORM_URL, '_blank').focus();
  };

  return (
    <div className="menu">
      <div className="menu__container">
        <div className="menu__exhibitions-container">
          {!!currentExhibitions.length && (
            <div className="menu__current">
              <h1 className="menu__period">{staticText.sidebarCurrent}</h1>
              {currentExhibitions.map((event) => (
                <MenuExhibition
                  key={event.id}
                  id={event.id}
                  name={event.name}
                  title={event.title}
                  type={event.type}
                  dateFrom={event.dateFrom}
                  dateTo={event.dateTo}
                  handleSelectExhibition={onSelectExhibition}
                  setIsSidebarOpen={setIsSidebarOpen}
                  language={language}
                />
              ))}
            </div>
          )}

          {!!upcomingEventsAndExhibitions.length && (
            <div className="menu__upcoming">
              <h1 className="menu__period">{staticText.sidebarUpcomming}</h1>
              {upcomingEventsAndExhibitions.map(
                ({ isEvent, isExhibition, ...rest }) =>
                  (isEvent && (
                    <MenuEvent
                      key={`upcoming-event-${rest.id}`}
                      id={rest.id}
                      name={rest.name}
                      title={rest.title}
                      type={rest.type}
                      startTime={rest.startTime}
                      endTime={rest.endTime}
                      date={rest.date}
                      handleSelectEvent={onSelectEvent}
                      setIsSidebarOpen={setIsSidebarOpen}
                      language={language}
                    />
                  )) ||
                  (isExhibition && (
                    <MenuExhibition
                      key={`upcoming-exhibition-${rest.id}`}
                      id={rest.id}
                      name={rest.name}
                      title={rest.title}
                      type={rest.type}
                      dateFrom={rest.dateFrom}
                      dateTo={rest.dateTo}
                      handleSelectExhibition={onSelectExhibition}
                      setIsSidebarOpen={setIsSidebarOpen}
                      language={language}
                    />
                  )),
              )}
            </div>
          )}
        </div>

        <div className="menu__media-container">
          {sectionLinks
            .filter(({ isVisible }) => isVisible)
            .map(({ section, title }) => (
              <h1
                key={section}
                className="menu__about"
                onClick={() => onClickSection(section)}
              >
                {title}
              </h1>
            ))}

          <div className="menu__newsletter">
            <h1 onClick={onClickJoinMailingList}>
              {staticText.sidebarJoinMailingList}
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
};

Menu.propTypes = {
  currentExhibitions: array,
  upcomingExhibitions: array,
  archiveExhibitions: array,
  upcomingEvents: array,
  setActiveSection: func.isRequired,
  educationItems: array,
  podcastItems: array,
  publicationItems: array,
  setIsSidebarOpen: func.isRequired,
  staticText: object,
  language: string.isRequired,
};

Menu.defaultProps = {
  currentExhibitions: [],
  upcomingExhibitions: [],
  archiveExhibitions: [],
  upcomingEvents: [],
  educationItems: [],
  podcastItems: [],
  publicationItems: [],
  staticText: {},
};

export default Menu;
